import { useState } from "react";
import { PDFDocument } from "pdf-lib";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Loader2, FilePlus2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { GenericToolShell } from "@/components/GenericToolShell";
import { FileDrop } from "@/components/crop/FileDrop";
import { Button } from "@/components/ui/button";
import pdfjs from "@/lib/crop/pdfjsSetup";

export default function MergePdf() {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const addFiles = async (list) => {
    const pdfs = list.filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (pdfs.length !== list.length) toast.error("Only PDF files are supported.");
    const added = [];
    for (const f of pdfs) {
      const bytes = new Uint8Array(await f.arrayBuffer());
      let pageCount = null;
      try { pageCount = (await pdfjs.getDocument({ data: bytes.slice() }).promise).numPages; } catch {}
      added.push({ id: `${f.name}-${Math.random()}`, name: f.name, size: f.size, bytes, pageCount });
    }
    setFiles((p) => [...p, ...added]);
  };

  const move = (i, dir) => setFiles((p) => {
    const n = [...p]; const j = i + dir;
    if (j < 0 || j >= n.length) return p;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const merge = async () => {
    if (files.length < 2) { toast.error("Add at least two PDFs to merge."); return; }
    setBusy(true);
    try {
      const out = await PDFDocument.create();
      for (const f of files) {
        let src;
        try { src = await PDFDocument.load(f.bytes.slice(), { ignoreEncryption: false }); }
        catch { throw new Error(`"${f.name}" could not be read (it may be password-protected).`); }
        const pages = await out.copyPages(src, src.getPageIndices());
        pages.forEach((pg) => out.addPage(pg));
      }
      const bytes = await out.save();
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "ecroply-merged.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Merged PDF downloaded!");
    } catch (e) {
      toast.error(e.message || "Could not merge the PDFs.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Seo title="Merge PDF Files Free - No Upload, No Login | Ecroply AI"
        description="Combine multiple PDF files into one, in any order you choose. Free, works entirely in your browser – files are never uploaded to a server."
        path="/tools/merge-pdf" />
      <GenericToolShell icon="FilePlus2" title="Merge PDF" promise="Combine several PDFs into one file, in the order you choose."
        privacy="Your PDF is processed in your browser on this device and is not uploaded to a server.">
        <FileDrop files={files} onAdd={addFiles} onRemove={(i) => setFiles((p) => p.filter((_, x) => x !== i))} hint="Add two or more PDFs"
          renderMeta={(f, i) => (
            <div className="flex gap-1 mt-1">
              <button onClick={(e) => { e.stopPropagation(); move(i, -1); }} className="p-1 rounded text-slate-400 hover:text-blue-600" data-testid={`move-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
              <button onClick={(e) => { e.stopPropagation(); move(i, 1); }} className="p-1 rounded text-slate-400 hover:text-blue-600" data-testid={`move-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
            </div>
          )} />
        <Button onClick={merge} disabled={busy || files.length < 2} className="w-full mt-6 h-12 bg-blue-600 hover:bg-blue-700" data-testid="merge-button">
          {busy ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Merging…</> : <><FilePlus2 className="w-5 h-5 mr-2" /> Merge PDFs</>}
        </Button>
      </GenericToolShell>
    </>
  );
}
